/**
 * Deduplicated Asset Upload Manager
 * 
 * Provides:
 * 1. Cryptographic SHA-256 deduplication to prevent uploading the same image twice to AWS S3.
 * 2. In-flight upload promise sharing so simultaneous pastes of the same image reuse a single upload.
 * 3. Awaitable barrier (`waitForAllInFlightUploads`) so saving before an AWS upload completes will
 *    wait for the upload to finish and store the real AWS URL in MongoDB rather than a broken blob URL.
 */

const assetCache = new Map<string, { url: string; assetId?: string }>();
const inFlightUploads = new Map<string, Promise<{ url: string; assetId?: string }>>();

/**
 * Computes a fast SHA-256 hex digest of any Blob or File.
 * Falls back to size + type + partial content if SubtleCrypto is unavailable.
 */
export async function computeBlobHash(blob: Blob): Promise<string> {
  try {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      const buffer = await blob.arrayBuffer();
      const digest = await window.crypto.subtle.digest('SHA-256', buffer);
      return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch (err) {
    console.warn('[AssetUploadManager] SubtleCrypto error, using fallback:', err);
  }
  return `fallback_${blob.size}_${blob.type}`;
}

/**
 * Uploads a file or blob with automatic deduplication.
 * If the exact same file is currently uploading or was already uploaded in this session,
 * it returns the existing AWS URL without creating duplicate uploads in S3.
 */
export async function uploadAssetDeduplicated(
  uploadMutation: (formData: FormData) => { unwrap: () => Promise<any> },
  fileOrBlob: Blob | File,
  fileName: string
): Promise<{ url: string; assetId?: string }> {
  const hash = await computeBlobHash(fileOrBlob);

  // 1. Check if already uploaded and cached
  const cached = assetCache.get(hash);
  if (cached) {
    return cached;
  }

  // 2. Check if currently uploading in-flight
  const existingPromise = inFlightUploads.get(hash);
  if (existingPromise) {
    return existingPromise;
  }

  // 3. Start a new upload
  const uploadPromise = (async () => {
    try {
      const formData = new FormData();
      formData.append('file', fileOrBlob, fileName);
      formData.append('name', fileName);

      const res = await uploadMutation(formData).unwrap();
      const asset = res?.data || res;
      const url = asset?.url;
      const assetId = asset?._id || asset?.id;

      if (!url) {
        throw new Error('Upload succeeded but no asset URL was returned by server');
      }

      const result = { url, assetId };
      assetCache.set(hash, result);
      return result;
    } finally {
      inFlightUploads.delete(hash);
    }
  })();

  inFlightUploads.set(hash, uploadPromise);
  return uploadPromise;
}

/**
 * Waits for all in-flight asset uploads to complete.
 * Used before saving a template to ensure all pasted or edited images have finished
 * uploading to AWS and their persistent cloud URLs are populated.
 */
export async function waitForAllInFlightUploads(): Promise<void> {
  if (inFlightUploads.size === 0) return;
  const promises = Array.from(inFlightUploads.values());
  await Promise.allSettled(promises);
}

